// src/features/user-portfolios/components/PortfolioPositionsTable.tsx
//
// Phase 5 — high-performance, headless ledger grid built on TanStack Table.
// TanStack provides the data-grid math (client-side sorting, column model,
// formatting) and renders ZERO CSS, so a user can click any column header
// (e.g. "Realized P&L") and instantly re-sort their tax-lots with no server
// round-trip or screen refresh. Styling is our Tailwind slate design system,
// theme-aware (light + dark) to match the rest of the app.

import { useMemo, useState } from 'react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import type { SortingState } from '@tanstack/react-table';
import { Link, useParams } from 'react-router-dom';

import { deletePortfolioPosition, type PositionRow } from '../api';
import {
    usePortfolioStore,
    type InstrumentType,
} from '../store/usePortfolioStore';
import { buildInstrumentRoute } from '../utils/instrumentRoute';

const columnHelper = createColumnHelper<PositionRow>();

// Currency-aware money formatter via Intl.NumberFormat. ``ccy`` is the asset's
// NATIVE currency for raw price columns (Average / Last Price) and the
// portfolio's BASE currency for the portfolio-lens columns (Cost Basis /
// Market Value / P&L), so global codes render as CA$, £, MX$, ¥... Falls back
// to an ISO-prefixed number for exotic/unknown codes instead of throwing.
const money = (val: number, ccy: string, maxDecimals = 2) => {
    const code = (ccy || 'USD').toUpperCase();

    // FIX: Explicitly intercept GBX so the browser doesn't auto-render "GBp".
    if (code === 'GBX') {
        return `GBX ${Number(val || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: maxDecimals,
        })}`;
    }

    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: code,
            minimumFractionDigits: 2,
            maximumFractionDigits: maxDecimals,
        }).format(Number(val) || 0);
    } catch {
        return `${code} ${Number(val || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: maxDecimals,
        })}`;
    }
};

const signedMoney = (val: number, ccy: string) => {
    const code = (ccy || 'USD').toUpperCase();
    const v = Number(val) || 0;
    const sign = v > 0 ? '+' : v < 0 ? '-' : '';

    if (code === 'GBX') {
        return `${sign}${money(Math.abs(v), code)}`;
    }

    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: code,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            signDisplay: 'exceptZero',
        }).format(v);
    } catch {
        return `${sign}${money(Math.abs(v), code)}`;
    }
};

const formatPct = (val: number) => `${Number(val || 0).toFixed(2)}%`;

const getColor = (val: number) =>
    val > 0
        ? 'text-emerald-600 dark:text-emerald-400'
        : val < 0
          ? 'text-red-600 dark:text-red-400'
          : 'text-slate-500 dark:text-slate-400';

export const PortfolioPositionsTable = ({
    positions,
}: {
    positions: PositionRow[];
}) => {
    const openModal = usePortfolioStore((s) => s.openModal);
    const { id: portfolioId } = useParams();

    // Default: biggest holdings first (base-currency Market Value desc).
    const [sorting, setSorting] = useState<SortingState>([
        { id: 'total_market_value_base', desc: true },
    ]);

    const columns = useMemo(
        () => [
            columnHelper.accessor('instrument', {
                header: 'Instrument',
                cell: (info) => {
                    const row = info.row.original;
                    return (
                        <Link
                            to={buildInstrumentRoute(row.symbol, row.instrument_type)}
                            className="font-bold whitespace-nowrap text-slate-900 transition hover:text-blue-600 hover:underline dark:text-white dark:hover:text-blue-400"
                        >
                            {info.getValue()}
                        </Link>
                    );
                },
            }),
            columnHelper.accessor('native_currency', {
                header: 'Ccy',
                cell: (info) => (
                    <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {info.getValue() || 'USD'}
                    </span>
                ),
            }),
            columnHelper.accessor('position', {
                header: 'Quantity',
                cell: (info) => {
                    const isCrypto =
                        info.row.original.instrument_type === 'CRYPTO';
                    return (
                        <span className="font-mono text-slate-600 dark:text-slate-300">
                            {Number(info.getValue()).toLocaleString(undefined, {
                                // 8 decimals for crypto (e.g. 0.00000005 BTC), 4 for stocks.
                                maximumFractionDigits: isCrypto ? 8 : 4,
                            })}
                        </span>
                    );
                },
            }),
            columnHelper.accessor('add_date', {
                header: 'Add Date',
                cell: (info) => (
                    <span className="whitespace-nowrap text-slate-500 dark:text-slate-400">
                        {info.getValue() ?? '—'}
                    </span>
                ),
            }),
            columnHelper.accessor('average_price_native', {
                header: 'Avg Price (Native)',
                cell: (info) => {
                    const row = info.row.original;
                    const isCrypto = row.instrument_type === 'CRYPTO';
                    const val = Number(info.getValue() || 0);
                    // Cheap crypto (e.g. SHIB at $0.000015/coin) needs extra
                    // decimals when the unit price is under $1.
                    const maxDecimals = isCrypto && val < 1 ? 6 : 2;
                    return (
                        <span className="font-mono text-slate-600 dark:text-slate-300">
                            {money(val, row.native_currency, maxDecimals)}
                        </span>
                    );
                },
            }),
            columnHelper.accessor('broker_fees_base', {
                header: 'Broker Fees',
                cell: (info) => (
                    <span className="font-mono text-slate-500 dark:text-slate-400">
                        {money(info.getValue(), info.row.original.base_currency)}
                    </span>
                ),
            }),
            columnHelper.accessor('cost_basis_base', {
                header: 'Cost Basis',
                cell: (info) => (
                    <span className="font-mono text-slate-600 dark:text-slate-300">
                        {money(info.getValue(), info.row.original.base_currency)}
                    </span>
                ),
            }),
            columnHelper.accessor('last_market_price_native', {
                header: 'Last Price (Native)',
                cell: (info) => {
                    const row = info.row.original;
                    const isCrypto = row.instrument_type === 'CRYPTO';
                    const val = Number(info.getValue() || 0);
                    const maxDecimals = isCrypto && val < 1 ? 6 : 2;
                    return (
                        <span className="font-mono text-slate-900 dark:text-white">
                            {money(val, row.native_currency, maxDecimals)}
                        </span>
                    );
                },
            }),
            columnHelper.accessor('total_market_value_base', {
                header: 'Market Value',
                cell: (info) => (
                    <span className="font-mono font-bold text-slate-900 dark:text-white">
                        {money(info.getValue(), info.row.original.base_currency)}
                    </span>
                ),
            }),
            columnHelper.accessor('daily_pnl_percent', {
                header: 'Change %',
                cell: (info) => {
                    const val = Number(info.getValue()) || 0;
                    return (
                        <span className={`font-mono font-medium ${getColor(val)}`}>
                            {val > 0 ? '+' : ''}
                            {formatPct(val)}
                        </span>
                    );
                },
            }),
            columnHelper.accessor('daily_pnl_currency_base', {
                header: 'Daily P&L',
                cell: (info) => {
                    const val = Number(info.getValue()) || 0;
                    return (
                        <span className={`font-mono font-medium ${getColor(val)}`}>
                            {signedMoney(val, info.row.original.base_currency)}
                        </span>
                    );
                },
            }),
            columnHelper.accessor('unrealized_pnl_base', {
                header: 'Unrealized P&L',
                cell: (info) => {
                    const val = Number(info.getValue()) || 0;
                    return (
                        <span className={`font-mono font-medium ${getColor(val)}`}>
                            {signedMoney(val, info.row.original.base_currency)}
                        </span>
                    );
                },
            }),
            columnHelper.accessor('unrealized_pnl_percent', {
                header: 'Unrealized %',
                cell: (info) => {
                    const val = Number(info.getValue()) || 0;
                    return (
                        <span className={`font-mono font-medium ${getColor(val)}`}>
                            {val > 0 ? '+' : ''}
                            {formatPct(val)}
                        </span>
                    );
                },
            }),
            columnHelper.accessor('realized_pnl_base', {
                header: 'Realized P&L',
                cell: (info) => {
                    const val = Number(info.getValue()) || 0;
                    return (
                        <span className={`font-mono ${getColor(val)}`}>
                            {signedMoney(val, info.row.original.base_currency)}
                        </span>
                    );
                },
            }),
            columnHelper.display({
                id: 'actions',
                header: 'Actions',
                cell: (info) => {
                    const row = info.row.original;

                    // Bug 1 FIX: Pre-fill with last_market_price (falling back to avg cost if 0)
                    // This way BOTH Edit and Sell use the current market reality by default.
                    const payload = {
                        symbol: row.symbol,
                        name: row.instrument_name ?? row.symbol,
                        type: (row.instrument_type ?? 'STOCK') as InstrumentType,
                        // Pre-fill with the NATIVE last price (the trade
                        // currency the Sell/Edit modal captures), falling back
                        // to the native average cost when no quote joined.
                        currentPrice:
                            row.last_market_price_native > 0
                                ? row.last_market_price_native
                                : row.average_price_native,
                        currentQuantity: row.position,
                    };

                    // Because the payload now holds the correct price, we can simplify the handlers
                    const handleSell = () => openModal(payload, 'SELL');
                    const handleEdit = () => openModal(payload, 'EDIT');
                    const handleDelete = async () => {
                        if (
                            !window.confirm(
                                `Delete ALL history for ${row.symbol}? This clears its P&L and cannot be undone.`,
                            )
                        )
                            return;
                        try {
                            await deletePortfolioPosition(
                                Number(portfolioId),
                                row.symbol,
                            );
                            window.dispatchEvent(new Event('portfolio-updated'));
                        } catch (err) {
                            // eslint-disable-next-line no-console
                            console.error(err);
                            alert('Could not delete this position. Please try again.');
                        }
                    };

                    return (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleSell}
                                className="rounded bg-amber-400/10 px-2 py-1 text-xs font-medium text-amber-600 transition hover:bg-amber-400/20 dark:text-amber-400"
                            >
                                Sell
                            </button>
                            <button
                                onClick={handleEdit}
                                className="px-1 text-xs font-medium text-indigo-600 transition hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                            >
                                Edit
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-1 text-xs font-medium text-red-600 transition hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                            >
                                Del
                            </button>
                        </div>
                    );
                },
            }),
        ],
        [openModal, portfolioId],
    );

    const table = useReactTable({
        data: positions,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (!positions || positions.length === 0) {
        return (
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 p-8 text-center text-slate-500 dark:text-slate-400">
                No open positions found. Add an asset to begin.
            </div>
        );
    }

    return (
        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
            <table className="w-full text-left text-sm">
                <thead className="select-none whitespace-nowrap border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                const sorted = header.column.getIsSorted();
                                // Pin the Instrument column so its label stays
                                // visible while the user scrolls the wide grid
                                // of numeric columns horizontally.
                                const isPinned = header.column.id === 'instrument';
                                return (
                                    <th
                                        key={header.id}
                                        className={`group cursor-pointer px-4 py-4 transition-colors hover:text-slate-900 dark:hover:text-white ${
                                            isPinned
                                                ? 'sticky left-0 z-20 bg-slate-50 dark:bg-slate-950 border-r border-slate-200 dark:border-slate-700'
                                                : ''
                                        }`}
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        <div className="flex items-center gap-1">
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext(),
                                            )}
                                            <span
                                                className={`w-3 text-blue-500 transition-opacity ${
                                                    sorted
                                                        ? 'opacity-100'
                                                        : 'opacity-0 group-hover:opacity-100'
                                                }`}
                                            >
                                                {sorted === 'asc'
                                                    ? '▲'
                                                    : sorted === 'desc'
                                                      ? '▼'
                                                      : '↕'}
                                            </span>
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                    {table.getRowModel().rows.map((row) => (
                        <tr
                            key={row.id}
                            className="group transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/60"
                        >
                            {row.getVisibleCells().map((cell) => {
                                const isPinned =
                                    cell.column.id === 'instrument';
                                return (
                                    <td
                                        key={cell.id}
                                        className={`px-4 py-3 ${
                                            isPinned
                                                ? 'sticky left-0 z-10 border-r border-slate-200 bg-white group-hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:group-hover:bg-slate-800/60'
                                                : ''
                                        }`}
                                    >
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext(),
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default PortfolioPositionsTable;
